import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import PageWrapper from '../components/PageWrapper';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { resellerApi } from '../api/reseller';

const faqs = {
  fr: [
    { q: 'Quelles sont les conditions pour devenir revendeur ?', a: 'Il suffit de remplir le formulaire avec votre numéro Fiscal, votre activité et vos volumes estimés. Notre équipe valide votre demande sous 48h ouvrées.' },
    { q: 'Quelles marges puis-je espérer ?', a: 'Les conditions tarif permettent généralement d\'aires revendeur vousappliquer des marges de 20 à 40% selon les gammes, avec des remises volume supplémentaires dès 50 unités.' },
    { q: 'Les délais de livraison sont-ils différents pour les revendeurs ?', a: 'Les comptes revendeurs bénéficient d\'une priorité de traitement des commandes et d\'accès à des stocks réservés pour les gammes à forte rotation.' },
    { q: 'Puis-je revendre dans plusieurs pays ?', a: 'Oui, LODISS distribue en Tunisie, Maghreb et en Europe. Les conditions commerciales sont adaptées selon la zone géographique.' },
  ],
  en: [
    { q: 'What are the conditions to become a reseller?', a: 'Simply fill in the form with your registration number, business activity and estimated volumes. Our team validates your request within 48 business hours.' },
    { q: 'What margins can I expect?', a: 'Reseller pricing conditions generally allow margins of 20 to 40% depending on the range, with additional volume discounts from 50 units.' },
    { q: 'Are delivery times different for resellers?', a: 'Reseller accounts benefit from priority order processing and access to reserved stock for fast-moving ranges.' },
    { q: 'Can I resell in multiple countries?', a: 'Yes, LODISS distributes in Tunisia, Maghreb and Europe. Commercial conditions are adapted according to geographic zone.' },
  ],
};

export default function ResellerPage() {
  usePageTitle("Devenir revendeur — Lodiss");

  const { lang, setPage } = useApp();
  const T = t[lang];

  const [form, setForm] = useState({
    company: '', tax: '', city: '', email: '', phone: '', volume: '', categories: [] as string[], message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const catOptions = lang === 'fr'
    ? ['Vêtements Pro', 'EPI & Sécurité', 'Batteries Pro']
    : ['Workwear', 'PPE & Safety', 'Pro Batteries'];

  const toggleCat = (cat: string) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(cat) ? f.categories.filter(c => c !== cat) : [...f.categories, cat],
    }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.company.trim()) e.company = lang === 'fr' ? 'Requis' : 'Required';
    if (!form.email.trim() || !form.email.includes('@')) e.email = lang === 'fr' ? 'Email invalide' : 'Invalid email';
    if (!form.phone.trim()) e.phone = lang === 'fr' ? 'Requis' : 'Required';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    
    try {
      setSubmitting(true);
      await resellerApi.submitApplication({
        company: form.company,
        tax_id: form.tax,
        city: form.city,
        email: form.email,
        phone: form.phone,
        monthly_volume: form.volume,
        categories: form.categories,
        message: form.message,
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit application:', err);
      setErrors({ submit: lang === 'fr' ? 'Erreur lors de la soumission' : 'Submission failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key: keyof typeof form, label: string, type = 'text', helper?: string) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <input
        type={type}
        placeholder={key === 'tax' ? '1234567A/B/000' : undefined}
        value={form[key] as string}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
          errors[key] ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-lodiss-red focus:ring-lodiss-red/20'
        }`}
      />
      {errors[key] && <p className="text-xs text-red-500 mt-1">{errors[key]}</p>}
      {helper && !errors[key] && <p className="text-xs text-gray-400 mt-1">{helper}</p>}
    </div>
  );

  return (
    <PageWrapper>
    <main className="bg-white min-h-screen">
      {/* Hero */}
      <section className="border-b border-gray-100 py-14 lg:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-lodiss-red text-xs font-semibold uppercase tracking-widest mb-3 block">
              {T.reseller.title}
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-950 mb-4">{T.reseller.subtitle}</h1>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {T.reseller.advantages.map((adv, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-5 hover:shadow-sm transition-shadow">
                <div className="w-8 h-8 bg-lodiss-red/10 rounded-lg flex items-center justify-center mb-3">
                  <Check size={16} className="text-lodiss-red" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{adv.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{adv.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Form */}
            <div>
              <h2 className="text-xl font-bold text-gray-950 mb-5">{T.reseller.form.title}</h2>
              {submitted ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex items-start gap-3">
                  <Check size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-emerald-800 font-medium">{T.reseller.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {field('company', T.reseller.form.company)}
                    {field('tax', T.reseller.form.tax, 'text', T.reseller.form.taxHelper)}
                    {field('city', T.reseller.form.city)}
                    {field('email', T.reseller.form.email, 'email')}
                    {field('phone', T.reseller.form.phone, 'tel')}
                    {field('volume', T.reseller.form.volume)}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">{T.reseller.form.categories}</label>
                    <div className="flex flex-wrap gap-2">
                      {catOptions.map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => toggleCat(cat)}
                          className={`px-3 py-1.5 text-xs font-medium border rounded-full transition-colors ${
                            form.categories.includes(cat)
                              ? 'border-lodiss-red bg-red-50 text-lodiss-red'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{T.reseller.form.message}</label>
                    <textarea
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-1 focus:ring-lodiss-red/20 transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3.5 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors text-sm"
                  >
                    {T.reseller.form.submit}
                  </button>
                </form>
              )}
            </div>

            {/* FAQ */}
            <div>
              <h2 className="text-xl font-bold text-gray-950 mb-5">FAQ</h2>
              <div className="space-y-2">
                {faqs[lang].map((faq, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-900 pr-3">{faq.q}</span>
                      {openFaq === i ? (
                        <ChevronUp size={16} className="text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    </PageWrapper>
  );
}
